import {
  Job,
  JobEntity,
  JobOperation,
  JobStatus,
  getAllJobs,
  ListJobParams,
} from '@commenda/api';

import { JOB_FETCHER_DEFAULT_OPTIONS, JOB_LIST_DEFAULT_SIZE } from './contant';

export interface JobWatcherState {
  jobs: Job[];
  refreshJobs: () => void;
  isJobInProgress: (entity: JobEntity) => boolean;
  isJobInProgressForOperation: (
    entity: JobEntity,
    operation: JobOperation
  ) => boolean;
  watchEntityWithRefresh: (
    entity: JobEntity,
    refreshFn: () => Promise<void> | void
  ) => () => void;
  getJobsByEntity?: (entity: JobEntity) => Job[];
  getJobsByEntityAndOperation?: (
    entity: JobEntity,
    operation: JobOperation
  ) => Job[];
  getJobsByEntityAndOperationAndEntityId?: (
    entity: JobEntity,
    operation: JobOperation,
    entityId: string
  ) => Job[];
}

export const useIndirectTaxJobWatcher = (
  params: ListJobParams
): JobWatcherState => {
  const { jobs, totalJobs, cursor, isLoading, error, mutate } = getAllJobs({
    params: {
      limit: JOB_LIST_DEFAULT_SIZE,
      ...params,
    },
    options: JOB_FETCHER_DEFAULT_OPTIONS,
  });

  const isJobInProgress = (entity: JobEntity): boolean => {
    if (!jobs) return false;

    return jobs.some(
      (job) =>
        job.entity === entity &&
        (job.status === JobStatus.PENDING ||
          job.status === JobStatus.PROCESSING ||
            job.status === JobStatus.QUEUED)
    );
  };

  const isJobInProgressForOperation = (
    entity: JobEntity,
    operation: JobOperation
  ): boolean => {
    if (!jobs) return false;

    return jobs.some(
      (job) =>
        job.entity === entity &&
        job.operation === operation &&
        (job.status === JobStatus.PENDING ||
          job.status === JobStatus.PROCESSING ||
          job.status === JobStatus.QUEUED)
    );
  };

  const getJobsByEntity = (entity: JobEntity): Job[] => {
    if (!jobs) return [];
    return jobs.filter((job) => job.entity === entity);
  };

  const getJobsByEntityAndOperation = (
    entity: JobEntity,
    operation: JobOperation
  ): Job[] => {
    if (!jobs) return [];
    return jobs.filter(
      (job) => job.entity === entity && job.operation === operation
    );
  };

  const watchEntityWithRefresh = (
    entity: JobEntity,
    refreshFn: () => Promise<void> | void
  ): (() => void) => {
    const intervalId = setInterval(async () => {
      if (isJobInProgress(entity)) {
        await refreshFn();
      } else {
        clearInterval(intervalId);
      }
    }, JOB_FETCHER_DEFAULT_OPTIONS.refreshInterval);

    return () => clearInterval(intervalId);
  };

  const getJobsByEntityAndOperationAndEntityId = (
    entity: JobEntity,
    operation: JobOperation,
    entityId: string
  ): Job[] => {
    if (!jobs) return [];
    return jobs.filter((job) => job.entity === entity && job.operation === operation && job.entity_id === entityId);
  };

  return {
    jobs,
    refreshJobs: mutate,
    isJobInProgress,
    isJobInProgressForOperation,
    watchEntityWithRefresh,
    getJobsByEntity,
    getJobsByEntityAndOperation,
    getJobsByEntityAndOperationAndEntityId,
  };
};
